// Pure pan/zoom math for the diagram viewport. No React — unit-testable.

export const MIN_SCALE = 1;
export const MAX_SCALE = 5;
export const SCALE_STEP = 0.25;

export function clampScale(scale) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/** Identity transform — fully zoomed out, centered. */
export function identityTransform() {
  return { scale: 1, x: 0, y: 0 };
}

/**
 * Zoom toward a focal point (cursor) so the point under the cursor stays put.
 * @param {{scale:number,x:number,y:number}} t  current transform
 * @param {number} factor   multiplicative zoom (>1 in, <1 out)
 * @param {number} fx       focal x within the viewport (px from left)
 * @param {number} fy       focal y within the viewport (px from top)
 */
export function zoomAtPoint(t, factor, fx, fy) {
  const next = clampScale(t.scale * factor);
  const applied = next / t.scale; // effective factor after clamping
  // Keep the focal point stationary: p = (f - x)/s must be invariant.
  // Translation bounding is the caller's job (it knows the viewport size).
  const x = fx - (fx - t.x) * applied;
  const y = fy - (fy - t.y) * applied;
  return { scale: next, x, y };
}

/** Step zoom centered on the viewport middle. */
export function zoomByStep(t, delta, viewportW, viewportH) {
  return zoomAtPoint(t, (t.scale + delta) / t.scale, viewportW / 2, viewportH / 2);
}

/**
 * Bound translation so the scaled content cannot be dragged entirely off-screen.
 * At scale 1 the only valid offset is 0. The slack grows with (scale-1)*size.
 */
export function clampTranslate(t, viewportW = 0, viewportH = 0) {
  const slackX = ((t.scale - 1) * viewportW) / 2 + viewportW * 0.15;
  const slackY = ((t.scale - 1) * viewportH) / 2 + viewportH * 0.15;
  return {
    scale: t.scale,
    x: clamp(t.x, -slackX, slackX),
    y: clamp(t.y, -slackY, slackY),
  };
}

function clamp(v, lo, hi) {
  if (lo > hi) return 0;
  return Math.min(hi, Math.max(lo, v));
}

export function isZoomed(t) {
  return t.scale > MIN_SCALE + 0.001;
}
